import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import { Form } from "react-bootstrap";
import { Table, DatePicker, Tag } from "antd";
import Instance from "../../AxiosConfig";

const PendingPaymentTable = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 50;

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currency, setCurrency] = useState("All");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  // ------------------ DEBOUNCE SEARCH ------------------
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  // ------------------ FETCH API ------------------
  const fetchPendingInvoices = async () => {
    try {
      setLoading(true);

      const response = await Instance.get("/Pendingpayments/pending", {
        params: {
          page: currentPage,
          limit: itemsPerPage,

          // ✅ FILTERS
          search: debouncedSearch || undefined,
          currency: currency !== "All" ? currency : undefined,
          startDate: fromDate || undefined,
          endDate: toDate || undefined,
        },
      });
console.log("Pending payments response:", response);  
      const blocks = response?.data?.data || [];

      const formattedData = blocks.map((item) => ({
        ...item,
        key: item._id,
      }));

      setData(formattedData);
      setTotalItems(response?.data?.total || 0);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // ------------------ RESET PAGE ON FILTER CHANGE ------------------
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, currency, fromDate, toDate]);

  // ------------------ API CALL ------------------
  useEffect(() => {
    fetchPendingInvoices();
  }, [currentPage, debouncedSearch, currency, fromDate, toDate]);

  // ------------------ CLEAR FILTERS ------------------
  const handleClearFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setCurrency("All");
    setFromDate(null);
    setToDate(null);
    setCurrentPage(1);
  };

  // ------------------ TABLE COLUMNS ------------------
  const columns = [
    {
      title: "Invoice No",
      dataIndex: "invoiceNo",
      width: 180,
      fixed: "left",
      render: (val) => <strong>{val}</strong>,
    },
    {
      title: "Company Name",
      dataIndex: "companyName",
      width: 350,
    },
    {
      title: "Agent",
      dataIndex: "agentName",
      width: 150,
    },
    {
      title: "Currency",
      dataIndex: "currency",
      width: 120,
      render: (currency) => (
        <Tag color={currency === "INR" ? "green" : "gold"}>
          {currency}
        </Tag>
      ),
    },
    {
      title: "Total Closure",
      dataIndex: "totalClosure",
      width: 180,
      render: (val, record) =>
        `${record.currency} ${(val || 0).toLocaleString()}`,
    },
     {
      title: "Term Amount",
      dataIndex: "termAmount",
      width: 160,
      render: (val, record) =>
        `${record.currency} ${(val || 0).toLocaleString()}`,
    },
    {
      title: "Pending Amount",
      dataIndex: "pendingAmount",
      width: 180,
      render: (val, record) => (
        <span style={{ color: "#ff4d4f", fontWeight: 600 }}>
          {record.currency} {(val || 0).toLocaleString()}
        </span>
      ),
    },
   
    {
      title: "Invoice Date",
      dataIndex: "invoiceDate",
      width: 150,
      render: (val) => dayjs(val).format("DD MMM YYYY"),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 140,
      render: (status) => {
        let bg = "#FDEAEA",
          color = "#FF4D4F";

        if (status === "Paid") {
          bg = "#DFF6E4";
          color = "#0CBC53";
        } 

        return (
          <span
            style={{
              backgroundColor: bg,
              color,
              padding: "4px 10px",
              borderRadius: "12px",
              fontWeight: 500,
            }}
          >
            {status}
          </span>
        );
      },
    },
  ];

  return (
    <>
      {/* FILTER */}
      <div className="filter-card p-3">
        <Form>
          <div className="row g-3">
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>Search</Form.Label>
                <Form.Control
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search invoice or company..."
                />
              </Form.Group>
            </div>

            <div className="col-md-2">
              <Form.Group>
                <Form.Label>Currency</Form.Label>
                <Form.Select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  <option>All</option>
                  <option>INR</option>
                  <option>USD</option>
                  <option>EUR</option>
                  <option>GBP</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-2">
              <Form.Label>From Date</Form.Label>
              <DatePicker
                className="form-control"
                value={fromDate ? dayjs(fromDate) : null}
                onChange={(d) =>
                  setFromDate(d ? d.format("YYYY-MM-DD") : null)
                }
              />
            </div>

            <div className="col-md-2">
              <Form.Label>To Date</Form.Label>
              <DatePicker
                className="form-control"
                value={toDate ? dayjs(toDate) : null}
                onChange={(d) =>
                  setToDate(d ? d.format("YYYY-MM-DD") : null)
                }
              />
            </div>

            <div className="col-md-1 d-flex align-items-end">
              <button
                type="button"
                className="clear-btn-company w-100"
                onClick={handleClearFilters}
              >
                Clear
              </button>
            </div>
          </div>
        </Form>
      </div>

      {/* TABLE */}
      <div className="dashboard mt-5">
        <h4 className="mb-4">Pending Payment List</h4>

        <Table
          rowKey="key"
          columns={columns}
          dataSource={data}
          loading={loading}
          scroll={{ x: "max-content" }} // ✅ width fix
          pagination={{
            current: currentPage,
            pageSize: itemsPerPage,
            total: totalItems,
            showSizeChanger: false,
          }}
          onChange={(pagination) => {
            setCurrentPage(pagination.current);
          }}
        />
      </div>
    </>
  );
};

export default PendingPaymentTable;